import { $, $$ } from '../../lib/dom';
import { icon } from '../../components/icons';

const DESKTOP_NAV_QUERY = '(min-width: 1101px)';

interface DrawerElements {
  readonly drawer: HTMLElement;
  readonly burger: HTMLButtonElement;
}

function elements(): DrawerElements | null {
  const drawer = $('#drawer');
  const burger = $('#burger', document, HTMLButtonElement);
  return drawer && burger ? { drawer, burger } : null;
}

const isOpen = (drawer: HTMLElement): boolean => drawer.classList.contains('open');

function openDrawer(): void {
  const els = elements();
  if (!els) return;
  els.drawer.hidden = false;
  els.drawer.classList.add('open');
  document.body.classList.add('locked');
  els.burger.setAttribute('aria-expanded', 'true');
  els.burger.setAttribute('aria-label', 'Close menu');
  els.burger.innerHTML = icon('x');
  els.drawer.focus();
}

export function closeDrawer({ restoreFocus = true }: { restoreFocus?: boolean } = {}): void {
  const els = elements();
  if (!els || !isOpen(els.drawer)) return;
  els.drawer.classList.remove('open');
  els.drawer.hidden = true;
  document.body.classList.remove('locked');
  els.burger.setAttribute('aria-expanded', 'false');
  els.burger.setAttribute('aria-label', 'Open menu');
  els.burger.innerHTML = icon('menu');
  if (restoreFocus) els.burger.focus();
}

/** Escape closes the drawer; Tab is trapped inside it while open. */
function onDocumentKeydown(event: KeyboardEvent): void {
  const els = elements();
  if (!els || !isOpen(els.drawer)) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeDrawer();
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = [els.burger, ...$$('a,button', els.drawer)];
  const index = focusable.findIndex((el) => el === document.activeElement);
  if (event.shiftKey && index <= 0) {
    event.preventDefault();
    focusable[focusable.length - 1].focus();
  } else if (!event.shiftKey && index === focusable.length - 1) {
    event.preventDefault();
    focusable[0].focus();
  }
}

let documentListenersBound = false;

/** Binds the drawer in the freshly rendered header. Document-level listeners are bound only once. */
export function initDrawer(): void {
  const els = elements();
  if (!els) return;
  els.burger.addEventListener('click', () => {
    if (isOpen(els.drawer)) closeDrawer();
    else openDrawer();
  });
  els.drawer.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('a')) closeDrawer({ restoreFocus: false });
  });
  if (documentListenersBound) return;
  documentListenersBound = true;
  document.addEventListener('keydown', onDocumentKeydown);
  window.matchMedia(DESKTOP_NAV_QUERY).addEventListener('change', (query) => {
    if (query.matches) closeDrawer({ restoreFocus: false });
  });
}
